import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
	Box,
	Divider,
	IconButton,
	Modal,
	Paper,
	Stack,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationCheck from '../../../ConfirmationCheck';
import ConfirmationSummary from './ConfirmationSummary';

function NewOrderConfirmationModal({ open, confirmation = null, onClose }) {
	const { t } = useTranslation();
	const items = confirmation?.items || [];

	return (
		<Modal
			open={open}
			onClose={onClose}
			aria-labelledby="new-order-confirmation-title"
			aria-describedby="new-order-confirmation-reference"
		>
			<Box
				sx={{
					position: 'absolute',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
					width: 'min(680px, calc(100vw - 32px))',
					maxHeight: 'min(760px, calc(100vh - 32px))',
					bgcolor: 'background.paper',
					boxShadow: 24,
					borderRadius: 2,
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						gap: 2,
						px: 3,
						pt: 2,
						pb: 1.5,
					}}
				>
					<Stack
						direction="row"
						spacing={1.5}
						sx={{ minWidth: 0, alignItems: 'center' }}
					>
						<ConfirmationCheck size="3.5rem" />
						<Box sx={{ minWidth: 0 }}>
							<Typography
								id="new-order-confirmation-title"
								variant="h4"
								sx={{
									color: 'text.primary',
									fontSize: '1.6rem',
									lineHeight: 1.1,
								}}
							>
								{t('new_orders:confirmation_modal:title')}
							</Typography>
							<Typography
								id="new-order-confirmation-reference"
								variant="body2"
								color="text.secondary"
								sx={{ mt: 0.5 }}
							>
								{t('new_orders:confirmation_modal:reference_po', {
									referencePo: confirmation?.referencePo || '-',
								})}
							</Typography>
						</Box>
					</Stack>
					<IconButton
						aria-label={t('new_orders:confirmation_modal:close_aria_label')}
						onClick={onClose}
					>
						<CloseIcon />
					</IconButton>
				</Box>

				<Divider />

				<Box
					sx={{
						p: 3,
						display: 'flex',
						flexDirection: 'column',
						gap: 3,
						minHeight: 0,
						flex: 1,
					}}
				>
					<Box sx={{ px: 0.5 }}>
						<ConfirmationSummary
							totalQuantity={confirmation?.totalQuantity || 0}
							totalProductCount={confirmation?.totalProductCount || 0}
						/>
					</Box>

					<Box
						sx={{
							minHeight: 0,
							overflowY: 'auto',
							px: 0.5,
							pt: 0.5,
							pb: 2,
						}}
					>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
							{t('new_orders:confirmation_modal:add_items')}
						</Typography>
						<Stack spacing={1.5}>
							{items.map((item) => (
								<Paper
									key={item.id}
									elevation={2}
									sx={{
										p: 2,
										borderRadius: 2,
										display: 'grid',
										gridTemplateColumns: 'auto 1fr auto',
										alignItems: 'center',
										gap: 2,
									}}
								>
									<ConfirmationCheck />
									<Box sx={{ minWidth: 0 }}>
										<Typography variant="body2" sx={{ fontWeight: 600 }}>
											{item.label}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											{item.designNumber}
										</Typography>
									</Box>
									<Typography variant="body2" sx={{ fontWeight: 600 }}>
										{t('new_orders:confirmation_modal:quantity', {
											quantity: item.quantity,
										})}
									</Typography>
								</Paper>
							))}
						</Stack>
					</Box>
				</Box>
			</Box>
		</Modal>
	);
}

NewOrderConfirmationModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	confirmation: PropTypes.shape({
		referencePo: PropTypes.string,
		totalQuantity: PropTypes.number,
		totalProductCount: PropTypes.number,
		items: PropTypes.arrayOf(
			PropTypes.shape({
				id: PropTypes.string.isRequired,
				value: PropTypes.string.isRequired,
				label: PropTypes.string.isRequired,
				designNumber: PropTypes.string,
				itemNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
					.isRequired,
				quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
					.isRequired,
			})
		),
	}),
};

export default React.memo(NewOrderConfirmationModal);
